import {
  ActionIcon,
  Card,
  Divider,
  Loader,
  LoadingOverlay,
  Pagination,
  Select,
  Text,
  TextInput,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import { useEffect, useLayoutEffect, useRef, useState, memo, useMemo } from "react";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import Icon from "src/components/common/Icons/Icons/Icon";
import { KnowledgeBaseDocumentType } from "src/interfaces/KnowledgeBase";

const TableRow = memo(({
  item,
  deleteSingleFile,
  editOneFile,
}: {
  key: number;
  item: KnowledgeBaseDocumentType;
  deleteSingleFile: (name: string, subject: string, is_qb?: boolean) => void;
  onPressDownload: (name: string) => void;
  selectedData: KnowledgeBaseDocumentType[];
  setSelectedData: (data: KnowledgeBaseDocumentType[]) => void | undefined;
  editOneFile: (name: string) => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) => {
  return (
    <div className="grid grid-cols-10 h-10 items-center px-4">
      <div className="col-span-4">
        <Text
          size={"xs"}
          fw={"600"}
          style={{
            maxWidth: "250px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.document_name}
        </Text>
      </div>
      <div className="col-span-2">
        <Text
          size={"xs"}
          fw={"600"}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.level}
        </Text>
      </div>
      <div className="col-span-2">
        <Text
          size={"xs"}
          fw={"600"}
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.subject}
        </Text>
      </div>
      <div className="col-span-1">
        <Text
          size={"xs"}
          fw={"600"}
          style={{
            overflow: "hidden",
            color: "blue",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.is_qb ? "QB" : "KB"}
        </Text>
      </div>
      <div className="col-span-1 flex gap-1">
        <ActionIcon onClick={() => editOneFile(item.document_name)}>
          <EditOutlined fontSize={"small"} />
        </ActionIcon>
        <ActionIcon
          onClick={() =>
            deleteSingleFile(item.document_name, item.subject, item.is_qb)
          }
        >
          <DeleteOutline fontSize={"small"} />
        </ActionIcon>
      </div>
    </div>
  );
});

TableRow.displayName = 'TableRow';

interface ScrollableTableProps {
  data: KnowledgeBaseDocumentType[];
  onRefresh: () => void;
  onSingleDelete: (name: string, subject: string, is_qb?: boolean) => void;
  onDownload: (name: string) => void;
  onDeleteFiles?: () => void;
  editOneFile: (name: string) => void;
  loading?: boolean;
}

export const ScrollableTable = (props: ScrollableTableProps) => {
  const { data, onRefresh, onDownload, onSingleDelete, editOneFile, loading = false } = props;
  const checkbox = useRef<HTMLInputElement | null>(null);
  const [selectedData, setSelectedData] = useState<KnowledgeBaseDocumentType[]>(
    []
  );
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const theme = useMantineTheme();
  const [isRendering, setIsRendering] = useState(false);

  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [fileType, setFileType] = useState<string>("all");
  const [level, setLevel] = useState<string>("all");
  const [subject, setSubject] = useState<string>("all");
  // Define type for select items
  type SelectItem = {
    value: string;
    label: string;
  };

  const [availableLevels, setAvailableLevels] = useState<SelectItem[]>([
    { value: "all", label: "All Levels" },
  ]);
  const [availableSubjects, setAvailableSubjects] = useState<SelectItem[]>([
    { value: "all", label: "All Subjects" },
  ]);
  const [isLoadingLevels, setIsLoadingLevels] = useState<boolean>(false);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState<boolean>(false);
  const [levelIdMap, setLevelIdMap] = useState<Record<string, number>>({});

  // Fetch levels from backend on mount
  useEffect(() => {
    const fetchLevels = async () => {
      try {
        setIsLoadingLevels(true);
        const response = await knowledgeBaseService.getLevels();
        if (response.kind === "ok") {
          // Create a map of level names to their IDs
          const newLevelIdMap: Record<string, number> = {};
          const formattedLevels = response.data.map((level) => {
            newLevelIdMap[level.name] = level.id;
            return {
              value: level.name,
              label: level.name,
            };
          });

          setLevelIdMap(newLevelIdMap);
          setAvailableLevels([
            { value: "all", label: "All Levels" },
            ...formattedLevels,
          ]);
        }
      } catch (error) {
        console.error("Error fetching levels:", error);
      } finally {
        setIsLoadingLevels(false);
      }
    };

    fetchLevels();
  }, []);

  // Fetch subjects when level changes
  useEffect(() => {
    const fetchSubjects = async () => {
      if (level === "all") {
        setAvailableSubjects([{ value: "all", label: "All Subjects" }]);
        return;
      }

      const levelId = levelIdMap[level];
      if (!levelId) return;

      try {
        setIsLoadingSubjects(true);
        const response = await knowledgeBaseService.getSubjects(levelId);
        if (response.kind === "ok") {
          const formattedSubjects = response.data.map((subject) => ({
            value: subject.name,
            label: subject.name,
          }));
          setAvailableSubjects([
            { value: "all", label: "All Subjects" },
            ...formattedSubjects,
          ]);
        } else {
          setAvailableSubjects([{ value: "all", label: "All Subjects" }]);
        }
      } catch (error) {
        console.error("Error fetching subjects:", error);
        setAvailableSubjects([{ value: "all", label: "All Subjects" }]);
      } finally {
        setIsLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [level, levelIdMap]);

  // Use useMemo to optimize filtering for large datasets
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const subjectName =
        typeof item.subject === "string"
          ? item.subject
          : item.document_name || "";
      const matchesQuery = query
        ? item.document_name.toLowerCase().includes(query.toLowerCase()) ||
          subjectName.toLowerCase().includes(query.toLowerCase())
        : true;

      const matchesType =
        fileType !== "all"
          ? fileType === "qb"
            ? item.is_qb
            : !item.is_qb
          : true;

      const matchesLevel =
        level !== "all" ? item.level === level : true;
      const matchesSubject =
        subject !== "all" ? item.subject === subject : true;

      return matchesQuery && matchesType && matchesLevel && matchesSubject;
    });
  }, [data, query, fileType, level, subject]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = useMemo(() => {
    return filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredData, currentPage]);

  // Detect when large datasets are being processed
  useEffect(() => {
    if (data.length > 0) {
      setIsRendering(true);
      // Use requestIdleCallback or setTimeout to detect when rendering is complete
      const timer = setTimeout(() => {
        setIsRendering(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [data]);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedData.length > 0 && selectedData.length < data.length;
    setChecked(selectedData.length === data.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) checkbox.current.indeterminate = isIndeterminate;
  }, [selectedData, data.length]);

  const isLoading = loading || isRendering;

  return (
    <>
      <Card
        p={0}
        withBorder
        className="h-96 overflow-y-scroll flex flex-col my-4 rounded-lg relative"
      >
        <LoadingOverlay visible={isLoading} overlayBlur={2} />
        <div
          className={`sticky top-0 flex-col flex py-2 z-40 ${
            theme.colorScheme == "dark" ? "bg-black" : "bg-white"
          } px-2`}
        >
          <div className="grid grid-cols-10 h-full items-center px-2">
            <div className="col-span-4">
              <div className="flex items-center">
                <Text size={"sm"} fw={600}>
                  File Name
                </Text>
              </div>
            </div>
            <div className="col-span-5">
              <div className="flex justify-between items-center">
                <TextInput
                  placeholder="Search files..."
                  value={query}
                  size="xs"
                  className="w-[100%] rounded-lg"
                  onChange={(event) => setQuery(event.currentTarget.value)}
                />
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              <ActionIcon onClick={onRefresh}>
                <ThemeIcon variant="light">
                  <Icon name="refresh" size="1rem" />{" "}
                </ThemeIcon>
              </ActionIcon>
            </div>
          </div>
          <div className="p-2 flex flex-wrap gap-2">
            <div className="flex-1 min-w-[120px]">
              <Select
                size="xs"
                placeholder="All Types"
                value={fileType}
                onChange={(value: string | null) => setFileType(value || "all")}
                data={[
                  { label: "All Types", value: "all" },
                  { label: "Question Bank", value: "qb" },
                  { label: "Knowledge Base", value: "kb" },
                ]}
                clearable
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <Select
                size="xs"
                placeholder="All Levels"
                value={level}
                onChange={(value: string | null) => {
                  setLevel(value || "all");
                  setSubject("all"); // Reset subject when level changes
                  // Reset subjects loading state when level changes
                  if (value !== level) {
                    setAvailableSubjects([
                      { value: "all", label: "All Subjects" },
                    ]);
                  }
                }}
                data={availableLevels}
                clearable
                disabled={isLoadingLevels}
              />
              {isLoadingLevels && <Loader size="xs" />}
            </div>
            <div className="flex-1 min-w-[150px]">
              <Select
                size="xs"
                // placeholder='All Subjects'
                value={subject}
                onChange={(value: string | null) => setSubject(value || "all")}
                data={availableSubjects}
                disabled={isLoadingSubjects || availableSubjects.length === 0}
                clearable
                placeholder={
                  isLoadingSubjects ? "Loading subjects..." : "All Subjects"
                }
              />
            </div>
          </div>
        </div>
        {data.length === 0 ? (
          <div className="flex flex-1 justify-center items-center py-6">
            <Text fw={600} size={"xs"}>
              You haven't uploaded any documents yet
            </Text>
          </div>
        ) : currentData.length > 0 ? (
          currentData.map((item, index) => (
            <div key={index}>
              <TableRow
                deleteSingleFile={onSingleDelete}
                onPressDownload={onDownload}
                key={index}
                item={item}
                selectedData={selectedData}
                setSelectedData={setSelectedData}
                editOneFile={editOneFile}
              />
              <Divider />
            </div>
          ))
        ) : (
          <div className="flex flex-1 justify-center items-center py-6">
            <Text fw={600} size={"xs"}>
              No documents match your search criteria
            </Text>
          </div>
        )}
      </Card>
      <div className=" w-full flex justify-center items-center">
        <Pagination
          total={totalPages}
          onChange={setCurrentPage}
          value={currentPage}
          color="dark"
          size="sm"
          radius="md"
          withControls={false}
          withEdges
        />
      </div>
    </>
  );
};
